import { useContext, useEffect, useRef } from 'react';


import { AbsoluteCenter, Box, useToast } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import {
  FRAuth,
  FRStep,
  FRUser,
  OAuth2Tokens,
  TokenManager,
} from '@forgerock/javascript-sdk/src/index';
import { useRouter } from 'next/router';

import CenteredLoader from '@/components/organism/dashboard/CenteredLoader/CenteredLoader';
import {
  CUSTOMERPORTAL_AUTH_INTENT_KEY,
  PREFERRED_LANG_COOKIE,
  REMIND_LATER_SURVEY_COOKIE,
  VENDOR_AUTH_INTENT_KEY,
  VENDOR_LOGIN_REDIRECT_COOKIE,
} from '@/constants/storage.constants';
import {
  checkAndMarkFirstLogin,
  decodeJwtSub,
  getDaysSinceLastLogin,
  getOrCreateSessionId,
  getStoredUtmParams,
  hashValue,
  isReactivatedUser,
  pushEvent,
  recordLoginTimestamp,
  setUserProperties,
} from '@/helpers/analytics';
import { getLocaleCode } from '@/helpers/locale';
import {
  clearAuthenticationSession,
  clearVendorAuthenticationSession,
  seedAuthenticationSession,
  seedVendorAuthenticationSession,
} from '@/helpers/session';
import { deleteCookie, getCookie, setCookie, urlArray } from '@/helpers/utils';
import useTranslation from '@/hooks/useTranslate';
import { useUAEPassSession } from '@/hooks/useUAEPassSession';
import { Context } from '@/providers/MainContext';
import { loginPreCheck } from '@/services/users';
import { AR, LocalizationTypes } from '@/types/localization.type';

/**
 * Single entry point of authentication
 * Handles authentication after redirect from UAEPass with approval from App
 */
const Authenticate = () => {
  const { setContextData } = useContext(Context);
  const localeRef = useRef<LocalizationTypes>(AR);
  const router = useRouter();
  const APP_URL = process.env.NEXT_PUBLIC_BASE_URL;
  const INVALID_CUSTOMER = 'You are not a valid customer';
  const UAE_PASS_SIGN_UP_LINK = 'https://selfcare.uaepass.ae/signup';
  const toast = useToast();
  const { translateByLocaleKey } = useTranslation();
  const { initiateUAE, isUaeInitated } = useUAEPassSession();

  /**
   * Handle auth failure
   * On auth failure logout, clear session and return to home page
   */
  const handleAuthFailure = async (errorMessage?: string) => {
    pushEvent('login_failure', { method: 'uaepass', error_type: errorMessage || 'unknown' });
    await logOut();
    router.push('/', '/', { locale: localeRef.current });

    if (errorMessage) {
      toast({
        title: errorMessage,
        status: 'error',
        duration: 10000,
        isClosable: true,
        icon: <></>,
      });
    }
  };

  /**
   * Handle response type LoginFailure
   */
  const handleLoginFailure = async () => {
    pushEvent('login_failure', { method: 'uaepass', error_type: 'login_failure' });
    await logOut();
    window.location.href = UAE_PASS_SIGN_UP_LINK;
    return;
  };

  /**
   * Handle response type Step
   * Redirect to external link of UAE pass creation if user is invalid
   * @param {FRStep} response
   */
  const handleResponseTypeStep = async (response: FRStep) => {
    if (
      response.payload.callbacks &&
      response.payload.callbacks[0].output[0].value === INVALID_CUSTOMER
    ) {
      // Auth failure with invalid customer
      await logOut(); // Incase !!!
      window.location.href = UAE_PASS_SIGN_UP_LINK;
      return;
    }

    // Incase !!!
    await logOut(); // Incase !!!
    router.push('/', '/', { locale: localeRef.current });
  };

  /**
   * Handle login success of UAEPass
   * Get the access token from the token manager and call for user eligibility
   * Return and navigate to non-eligible page if user is not eligible
   * Set and store the authentication session state on cookies
   * Navigate to home page if onboarding completed - scoped out
   * Navigate to onboarding page if onboarding is not complete - scoped out
   */
  const handleAuthSuccess = async () => {
    try {
      // Vendor flow: mirrors customer flow but skips loginPreCheck/eligibility
      const isVendorFlow =
        localStorage.getItem(VENDOR_AUTH_INTENT_KEY) === 'true';
      if (isVendorFlow) {
        const vendorTokens = await TokenManager.getTokens({ forceRenew: true });
        const { accessToken: vendorAccessToken } = vendorTokens as OAuth2Tokens;

        seedVendorAuthenticationSession();

        const vendorLocale = localeRef.current;
        setCookie(PREFERRED_LANG_COOKIE, vendorLocale);
        setContextData({
          locale: vendorLocale,
          is_rtl: vendorLocale === AR,
        });

        const vendorUtmParams = getStoredUtmParams();
        const vendorIsFirstLogin = checkAndMarkFirstLogin();
        const vendorDaysSinceLastLogin = getDaysSinceLastLogin();
        const vendorReactivated = isReactivatedUser();

        const vendorSub = decodeJwtSub(vendorAccessToken);
        const vendorHashedUserId = vendorSub
          ? await hashValue(vendorSub)
          : undefined;
        const vendorSessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

        pushEvent('login_success', {
          method: 'uaepass',
          portal: 'vendor',
          is_first_login: vendorIsFirstLogin,
          ...(vendorHashedUserId ? { user_id: vendorHashedUserId } : {}),
          session_id: vendorSessionId,
          ...vendorUtmParams,
        });

        if (vendorReactivated) {
          pushEvent('user_reactivated', {
            days_since_last_login: vendorDaysSinceLastLogin,
            reactivation_source: vendorUtmParams.utm_source || 'direct',
          });
        }

        const vendorNowIso = new Date().toISOString().split('T')[0];
        setUserProperties({
          last_login_date: vendorNowIso,
          ...(vendorIsFirstLogin ? { registration_date: vendorNowIso } : {}),
          ...(vendorUtmParams.utm_source
            ? { acquisition_source: vendorUtmParams.utm_source }
            : {}),
        });

        recordLoginTimestamp();
        deleteCookie(REMIND_LATER_SURVEY_COOKIE);
        getOrCreateSessionId();
        localStorage.removeItem(CUSTOMERPORTAL_AUTH_INTENT_KEY);

        const vendorRedirectUrl = getCookie(VENDOR_LOGIN_REDIRECT_COOKIE);
        setCookie(VENDOR_LOGIN_REDIRECT_COOKIE, '');
        const vendorTargetPath = vendorRedirectUrl
          ? decodeURIComponent(vendorRedirectUrl)
          : '/vendor/registration';

        router.push(vendorTargetPath, vendorTargetPath, {
          locale: vendorLocale,
        });
        return;
      }

      const tokens = await TokenManager.getTokens({
        forceRenew: true,
      });
      const { accessToken } = tokens as OAuth2Tokens;
      const source = 'web';

      const loginPreCheckResponse = await loginPreCheck(
        accessToken,
        source
      ).catch(error => {
        console.error(
          '---  Error message ---',
          error?.response?.data?.error?.message
        );
        datadogLogs.logger.info('Error while authenticating user');
        return null;
      });

      if (!loginPreCheckResponse) {
        await handleAuthFailure();
        return;
      }

      const {
        isEligible,
        isUAEPassUpgraded,
        preferredLanguage: userLocale,
      } = loginPreCheckResponse.data.data;

      const preferredLanguage = getLocaleCode(userLocale);
      setCookie(PREFERRED_LANG_COOKIE, preferredLanguage);
      setContextData({
        locale: preferredLanguage,
        is_rtl: preferredLanguage === AR,
      });
      if (!isUAEPassUpgraded || !isEligible) {
        await logOut();
        const redirectPath = !isUAEPassUpgraded
          ? '/sign-up/non-upgraded'
          : '/sign-up/non-eligible';
        router.push(redirectPath, redirectPath, {
          locale: localeRef.current,
        });
        return;
      }

      // Set the authentication session
      seedAuthenticationSession(true);

      const utmParams = getStoredUtmParams();
      const isFirstLogin = checkAndMarkFirstLogin();
      const daysSinceLastLogin = getDaysSinceLastLogin();
      const reactivated = isReactivatedUser();

      const sub = decodeJwtSub(accessToken);
      const hashedUserId = sub ? await hashValue(sub) : undefined;
      const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      pushEvent('login_success', {
        method: 'uaepass',
        is_first_login: isFirstLogin,
        ...(hashedUserId ? { user_id: hashedUserId } : {}),
        session_id: sessionId,
        ...utmParams,
      });

      if (reactivated) {
        pushEvent('user_reactivated', {
          days_since_last_login: daysSinceLastLogin,
          reactivation_source: utmParams.utm_source || 'direct',
        });
      }

      const nowIso = new Date().toISOString().split('T')[0];
      setUserProperties({
        last_login_date: nowIso,
        ...(isFirstLogin ? { registration_date: nowIso } : {}),
        ...(utmParams.utm_source ? { acquisition_source: utmParams.utm_source } : {}),
      });

      recordLoginTimestamp();
      deleteCookie(REMIND_LATER_SURVEY_COOKIE);

      localStorage.removeItem(VENDOR_AUTH_INTENT_KEY);

      const userRedirectUrl = getCookie('teyaseer_login.redirectUrl');

      if (userRedirectUrl) {
        // parse the url and remove the cookie
        setCookie('login.redirectUrl', '');
        // in the userRedirectUrl we have all kind of %20 and etc, so we need to decode it.
        const decodedUrl = decodeURIComponent(userRedirectUrl);
        const redirectUrl = decodedUrl.replace(`/${preferredLanguage}`, '');
        const url =
          redirectUrl && urlArray.some(route => redirectUrl.startsWith(route))
            ? redirectUrl
            : '/dashboard';
        router.push(url, url, {
          locale: preferredLanguage,
        });
        return;
      } else {
        router.replace('/dashboard', '/dashboard', {
          locale: preferredLanguage,
        });
      }
    } catch (error) {
      console.error('-------TOKEN GET FAILURE--------', error);
      handleAuthFailure();
    }
  };

  /**
   * Handle authentication if router is ready
   * If query param has error return with auth failure handler
   * If query param has code and state resume the auth process with actual URL
   * Switch to different response type handler
   */
  const handleAuthentication = async () => {
    if (router.query.error) {
      console.error('---UAEPassLoginCancelled---', router.query.error);
      const errorMessage = translateByLocaleKey(
        localeRef.current,
        'error_user_cancel_login'
      );
      handleAuthFailure(errorMessage);
      return;
    }

    if (router.query.code && router.query.state && !isUaeInitated()) {
      try {
        initiateUAE(true);

        const response = await FRAuth.resume(APP_URL + router.asPath);
        const { type } = response;
        switch (type) {
          case 'Step':
            await handleResponseTypeStep(response);
            break;
          case 'LoginSuccess':
            await handleAuthSuccess();
            break;
          case 'LoginFailure':
            await handleLoginFailure();
            break;
          default:
            await handleAuthFailure();
            break;
        }
      } catch (error) {
        console.error('-------AUTH ERROR--------', error);
        initiateUAE(false);
        handleAuthFailure();
      }
    } else {
      datadogLogs.logger.info('Error while authenticating user');
    }
  };

  useEffect(() => {
    localeRef.current =
      (localStorage.getItem('locale') as LocalizationTypes) || AR;
  }, []);

  useEffect(() => {
    if (router?.isReady) handleAuthentication();
  }, [router.query.code, router.query.state]);

  return (
    <Box position="relative" h="100vh">
      <AbsoluteCenter axis="both">
        <CenteredLoader />
      </AbsoluteCenter>
    </Box>
  );
};
export const logOut = async () => {
  const preferredLanguage = getCookie(PREFERRED_LANG_COOKIE) || AR;
  clearAuthenticationSession();
  clearVendorAuthenticationSession();
  localStorage.removeItem(VENDOR_AUTH_INTENT_KEY);
  localStorage.removeItem(CUSTOMERPORTAL_AUTH_INTENT_KEY);
  await FRUser.logout();
  setCookie(PREFERRED_LANG_COOKIE, preferredLanguage);
};

export default Authenticate;
